// @flow 
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard';
import SubscriptionReadOnlyNotice from '../../components/subscription-read-only-notice';
import { useUserContext } from '../../context';
import { useState, useEffect } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Dialog,
    DialogBody,
    Typography
} from "@material-tailwind/react";
import { ClockLoader } from 'react-spinners';


export default function ReturnLaundry() {
    const { basketId } = useParams();
    const navigate = useNavigate();
    const { fetchAccessToken, defaultHotel, isAdmin, isLaundryManager } = useUserContext();
    const { canEdit, ensureCanEdit } = useSubscriptionGuard();
    const { getLaundryItems, getLaundry, updateLaundry } = useApi();
    const [laundry, setLaundry] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [laundryItems, setLaundryItems] = useState([]);
    const [basketReturnDate, setBasketReturnDate] = useState(() => formatDate());


    useEffect(() => {

        let getLaundryInternal = async (hotelId, basketId) => {
            setIsLoading(true);
            try {
                const token = await fetchAccessToken();
                const lItems = await getLaundryItems(hotelId, token);
                const data = await getLaundry(hotelId, basketId, token);
                if (lItems && lItems.length) {
                    setLaundryItems(lItems);
                } else {
                    setLaundryItems([]);
                }
                if (data) {
                    setLaundry(data);
                } else {
                    setLaundry(null);
                    alert("Laundry basket doesn't exist.");
                    navigate(`/${uriPart}/laundry`);
                }
            } catch (ex) {
                alert("Something went wrong.");
            } finally {
                setIsLoading(false);
            }
        }
        if (defaultHotel && basketId) {
            getLaundryInternal(defaultHotel, basketId);
        }
    }, [defaultHotel, basketId]);

    let handleCanelInternal = () => {
        const result = window.confirm("Do you want to discard the return?");
        if (result) {
            navigate(`/${uriPart}/laundry`);
        }
    };

    function formatDate() {
        const date = new Date();
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    };

    function formatToDdMmYyyy(dateStr) {
        const date = new Date(dateStr);
        if (isNaN(date)) {
            return dateStr;
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }

    function getCurrentTime(hoursAdd = 0) {
        const now = new Date();
        const hours = String(now.getHours() + hoursAdd).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return hours + ':' + minutes;
    }

    let handleSubmitInternal = async () => {
        if (!ensureCanEdit()) return;
        try {
            setIsLoading(true);
            let missingItems = [];
            for (const laundryItem of laundryItems) {
                let missingCount = getMissingItemCount(laundryItem.itemName);
                if (missingCount > 0) {
                    missingItems = [...missingItems, { name: laundryItem.itemName, count: missingCount, unitPrice: laundryItem.unitCharge }]
                }
            }
            laundry.returnDate = formatToDdMmYyyy(basketReturnDate);
            laundry.returnTime = getCurrentTime();
            laundry.status = "RETURN";
            laundry.missingItems = missingItems;
            if (missingItems.length > 0) {
                let confirm = window.confirm("Few items missing in return items. Do you want to proceed");
                if (!confirm) {
                    setIsLoading(false);
                    return;
                }
                laundry.remarks = laundry.remarks + "," + "Missing items in the return basket";
            }
            const token = await fetchAccessToken();
            await updateLaundry(defaultHotel, laundry, token);
            alert("Return has been created.");
            navigate(`/${uriPart}/laundry`);
        } catch (ex) {
            alert("Error occurred while creating return basket." + ex);
        } finally {
            setIsLoading(false);
        }
    }

    let getPreviousMissingItemCount = (itemName) => {
        const missingItem = laundry.previousMissedItems.find(item => item.name === itemName);
        if (missingItem) {
            return missingItem.count;
        }
        return 0;
    };

    let getItemCount = (itemName) => {
        const missingItem = laundry.pickupItems.find(item => item.name === itemName);
        if (missingItem) {
            return missingItem.count;
        }
        return 0;
    };

    let getReturnItemCount = (itemName) => {
        const missingItem = laundry.returnItems.find(item => item.name === itemName);
        if (missingItem) {
            return missingItem.count;
        }
        return 0;
    };

    let setReturnItemCount = (itemName, count, unitCharge) => {
        const returnItems = laundry.returnItems;
        let updatedItems = [];
        const itemIndex = returnItems.findIndex((item) => item.name === itemName);
        if (itemIndex !== -1) {
            updatedItems = [...returnItems];
            updatedItems[itemIndex] = {
                ...updatedItems[itemIndex],
                count: count,
            };
        } else {
            updatedItems = [...returnItems, { name: itemName, count: count, unitPrice: unitCharge }];
        }
        setLaundry({ ...laundry, returnItems: updatedItems })
    };

    let getMissingItemCount = (itemName) => {
        const previous = getPreviousMissingItemCount(itemName);
        const current = getItemCount(itemName);
        const r = getReturnItemCount(itemName) || 0;
        return previous + current - r;
    };

    let uriPart = isLaundryManager ? "laundry-manager" : "manager";

    return (
        <div>
            <Card className="h-full w-full">
                <CardHeader variant="gradient" color="gray" className="mt-6 mb-8 p-6 flex flex-row justify-between">
                    <Typography variant="h6" color="white">
                        Return
                    </Typography>
                </CardHeader>
                <CardBody>
                    <div className="relative w-full mb-3">
                        {isAdmin && <div className="w-full lg:w-4/12 px-4">
                            <div className="h-10 flex justify-between sm:justify-start">
                                <Typography className="mt-2">
                                    Return Date:
                                </Typography>
                                <div className="flex flex-row">
                                    <input type="date" className="border-0  ml-2 mr-2  px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        value={basketReturnDate} onChange={(event) => setBasketReturnDate(event.target.value)} />
                                </div>
                            </div>
                        </div>}
                        <div className="relative w-full mb-3">
                            <div className="flex flex-wrap gap-1 items-center mb-3">
                                <div className="flex-1 min-w-[50px] border-r">
                                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Item Name</label>
                                </div>
                                <div className="flex-1 min-w-[50px] border-r">
                                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Arrears</label>
                                    +
                                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Pickup</label>
                                </div>
                                <div className="flex-1 min-w-[50px] border-r">
                                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Return <span className="text-red-500">*</span></label>
                                </div>
                                <div className="flex-1 min-w-[50px]">
                                    <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Missing</label>
                                </div>
                            </div>
                            {laundry && laundryItems.map((laundryItem, index) =>
                                <div className="flex flex-wrap gap-1 items-center mb-3" key={index}>
                                    <div className="flex-1 min-w-[50px] border-r">
                                        <Typography
                                            variant="small"
                                            className="flex items-center font-normal text-blue-gray-600"
                                        >
                                            {laundryItem.itemName}
                                        </Typography>
                                    </div>
                                    <div className="flex-1 min-w-[50px] border-r ">
                                        <label className="block text-blueGray-600 text-md font mt-1 ml-2">
                                            {getPreviousMissingItemCount(laundryItem.itemName)}
                                            {" + "}
                                            {getItemCount(laundryItem.itemName)}
                                        </label>
                                    </div>
                                    <div className="flex-1 min-w-[50px] border-r">
                                        <input type="number"
                                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                            value={getReturnItemCount(laundryItem.itemName)} onChange={(event) => setReturnItemCount(laundryItem.itemName, parseInt(event.target.value, 10), laundryItem.unitCharge)} />
                                    </div>
                                    <div className="flex-1 min-w-[50px]">
                                        <label className="block text-blueGray-600 text-md font mt-1 ml-2">{getMissingItemCount(laundryItem.itemName)}</label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <SubscriptionReadOnlyNotice className="mt-6" />
                        <div className="w-full flex justify-end  mb-0 py-6">
                            {canEdit && <button className="bg-blue-500 text-white hover:bg-blue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150" type="button" onClick={handleSubmitInternal}>
                                Return
                            </button>}
                            <button className="bg-pink-500 text-white hover:bg-pink-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150" type="button" onClick={handleCanelInternal}>
                                Canel
                            </button>
                        </div>
                    </div>
                </CardBody>
            </Card>
            <Dialog open={isLoading} className="bg-transparent shadow-none" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>
        </div>
    );
};